import type { GameEvent } from '../../game/types'
import type { Lang } from '../../i18n/strings'

/*
 * Dataset localization, lazy-loaded.
 *
 * Translations live as one JSON file per language per category in this folder
 * (e.g. ./tr/football.json — an array of { id, title, blurb }). English is the
 * canonical source (src/data/*.json) and the fallback. Each language's files
 * are code-split into their own chunks and fetched only when that language is
 * selected, so the initial bundle stays English-only.
 */

interface TrEntry {
  id: string
  title: string
  blurb?: string
}
export type TrMap = Record<string, TrEntry>

const loaders = import.meta.glob('./*/*.json') as Record<
  string,
  () => Promise<{ default: TrEntry[] }>
>

const cache: Partial<Record<Lang, TrMap>> = {}

/** Loads (and caches) the id→{title,blurb} map for a language. English → empty. */
export async function loadTranslations(lang: Lang): Promise<TrMap> {
  if (lang === 'en') return {}
  if (cache[lang]) return cache[lang] as TrMap
  const map: TrMap = {}
  const mods = await Promise.all(
    Object.entries(loaders)
      .filter(([path]) => path.startsWith(`./${lang}/`))
      .map(([, load]) => load()),
  )
  for (const m of mods) for (const e of m.default) if (e && e.id) map[e.id] = e
  cache[lang] = map
  return map
}

/** Swaps an event's title/blurb using a loaded map (English fallback if absent). */
export function applyTranslation(ev: GameEvent, map: TrMap | undefined): GameEvent {
  const tr = map?.[ev.id]
  return tr ? { ...ev, title: tr.title, blurb: tr.blurb ?? ev.blurb } : ev
}
